import React, { useEffect, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { getCurrentUser } from "aws-amplify/auth";
import { createLabResults } from "../graphql/mutations.js";

const client = generateClient();

const labsFields = [
  { key: "LabsTG", label: "Triglycerides", range: "< 150 mg/dL" },
  { key: "LabsTC", label: "Cholesterol (Total)", range: "< 200 mg/dL" },
  {
    key: "LabsHDL",
    label: "Cholesterol (High-Density Lipoprotein)",
    range: "> 39 mg/dL",
  },
  {
    key: "LabsLDL",
    label: "Cholesterol (Low-Density Lipoprotein)",
    range: "< 100 mg/dL",
  },
  { key: "LabsNa", label: "Sodium", range: "134 – 144 mmol/L" },
  { key: "LabsK", label: "Potassium", range: "3.5 – 5.2 mmol/L" },
  { key: "LabsP", label: "Phosphorus", range: "2.5 – 4.5 mg/dL" },
  { key: "LabsCl", label: "Chloride", range: "96 – 106 mmol/L" },
  { key: "LabsZn", label: "Zinc", range: "44 – 115 mcg/dL" },
  { key: "LabsCa", label: "Calcium", range: "8.7 – 10.2 mg/dL" },
  { key: "LabsMg", label: "Magnesium", range: "1.6 – 2.3 mg/dL" },
  { key: "LabsFe", label: "Iron", range: "149 – 505 mcg/dL" },
  { key: "LabsFerritin", label: "Ferritin", range: "15 – 150 ng/mL" },
  { key: "LabsVitA", label: "Vitamin A", range: "20.1 – 62 mcg/dL" },
  {
    key: "LabsVitB1",
    label: "Thiamin (B1)",
    range: "2.5 – 7.5 μg/dL OR 74 – 222 nmol/L",
  },
  {
    key: "LabsVitB2",
    label: "Riboflavin (B2)",
    range: "106 – 638 nmol/L",
  },
  { key: "LabsVitB3", label: "Niacin (B3)", range: "0.50 – 8.45 μg/mL" },
  {
    key: "LabsVitB5",
    label: "Pantothenic Acid (B5)",
    range: "1.6 – 2.7 μmol/L",
  },
  { key: "LabsVitB6", label: "Pyridoxine (B6)", range: "5 – 50 μg/L" },
  {
    key: "LabsVitB7",
    label: "Biotin (B7)",
    range: "133 – 329 pmol/L",
  },
  { key: "LabsVitB9", label: "Folate (B9)", range: "> 3 ng/mL" },
  {
    key: "LabsVitB12",
    label: "Cobalamin (B12)",
    range: "232 – 1245 pg/mL",
  },
  { key: "LabsVitC", label: "Vitamin C", range: "0.4 – 2 mg/L" },
  {
    key: "LabsVitD",
    label: "Vitamin D, 25-Hydroxy",
    range: "30.0 – 100.0 ng/mL",
  },
  {
    key: "LabsVitE",
    label: "Vitamin E (Alpha Tocopherol)",
    range: "7 – 25.1 mg/L",
  },
  {
    key: "LabsVitK",
    label: "Vitamin K",
    range: "0.22 – 4.88 nmol/L",
  },
];

const inputStyle = {
  width: "100%",
  padding: "10px",
  borderRadius: 12,
  border: "2px solid #ccc",
  fontSize: 14,
  boxSizing: "border-box",
};

const labelStyle = {
  fontWeight: "normal",
  color: "#333",
  marginBottom: 4,
};

export default function LabResultsAssessment() {
  const [userId, setUserId] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Only need userId now (no prefill / no existing record)
  useEffect(() => {
    let isMounted = true;

    async function init() {
      try {
        const { userId } = await getCurrentUser();
        if (!isMounted) return;
        setUserId(userId);
      } catch (err) {
        console.error("Error loading user:", err);
        setErrorMessage("Error loading user.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    init();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!userId) {
      setErrorMessage("User not loaded.");
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const now = new Date();
      const input = {
        userId,
        CompletedDate: now.toISOString().slice(0, 10),
        CompletedTime: now.toLocaleTimeString(),
        isActive: true,
      };

      // Convert values to Float (or null if empty/invalid)
      labsFields.forEach(({ key }) => {
        const raw = formData[key];
        if (raw === undefined || raw === null || raw === "") {
          input[key] = null;
        } else {
          const num = parseFloat(raw);
          input[key] = Number.isFinite(num) ? num : null;
        }
      });

      // Always create a new record
      await client.graphql({
        query: createLabResults,
        variables: { input },
      });

      setSuccessMessage("Lab results saved successfully.");

      // Reset form so they can enter a new set
      setFormData({});
    } catch (err) {
      console.error("Error saving lab results:", err);
      setErrorMessage("Failed to save lab results.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          maxWidth: 800,
          margin: "auto",
          padding: 24,
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        }}
      >
        Loading lab results…
      </div>
    );
  }

  return (
    <div>
      <div
        className="labs-box"
        style={{
          minWidth: 350,
          margin: "0 auto",
          backgroundColor: "white",
          padding: 24,
          borderRadius: 12,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          color: "#333",
        }}
      >
        <img
          src={(process.env.PUBLIC_URL || "") + "/Icon-Lab-Blue.png"}
          alt="Logo"
          style={{
            width: "100%",
            maxWidth: 200,
            height: "auto",
            display: "block",
            margin: "10px auto 30px auto",
          }}
        />
        <h1 style={{ marginBottom: 8, color: "#333" }}>Lab Results</h1>
        <p style={{ marginBottom: 24, fontSize: 14, color: "#333" }}>
          Please enter your most recent lab results.
        </p>

        <hr style={{ border: "2px solid #ccc", margin: "24px 0" }} />

        {labsFields.map(({ key, label, range }) => (
          <div
            key={key}
            style={{
              marginBottom: 18,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <label htmlFor={key} style={labelStyle}>
              {label}
            </label>
            <input
              id={key}
              type="text"
              value={formData[key] || ""}
              onChange={(e) => handleChange(key, e.target.value)}
              style={inputStyle}
              placeholder="Enter value (e.g. 145)"
            />
          </div>
        ))}

        <hr style={{ border: "1px solid #ccc", margin: "24px 0" }} />

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            width: "100%",
            backgroundColor: "#0067B3",
            color: "white",
            fontWeight: "700",
            fontSize: 16,
            borderRadius: 8,
            padding: "12px 0",
            border: "none",
            cursor: "pointer",
            userSelect: "none",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          }}
        >
          {isSubmitting ? "Submitting..." : "Submit Lab Results"}
        </button>

        {successMessage && (
          <div
            style={{
              marginTop: 12,
              color: "#0a7a2f",
              backgroundColor: "#e6ffed",
              padding: 10,
              borderRadius: 8,
              fontSize: 14,
              textAlign: "center",
            }}
          >
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div
            style={{
              marginTop: 12,
              color: "#cc0000",
              backgroundColor: "#ffeeee",
              padding: 10,
              borderRadius: 8,
              fontSize: 14,
              textAlign: "center",
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
}
